import { Card, List, Typography, Space, Button, Empty, Tag } from 'antd'
import {
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  DownloadOutlined,
  EyeOutlined,
  ExperimentOutlined,
} from '@ant-design/icons'

const { Text } = Typography

interface LabResult {
  id: string
  labName: string
  sampleDate: string
  status: string
  reportUrl?: string | null
  documents?: Array<{
    id: string
    fileName: string
    fileUrl: string
    fileType: string
    fileSize: number | null
    createdAt: string
  }>
}

function formatDate(dateString: string | null) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('sr-Latn-RS', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function formatFileSize(bytes: number | null) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const statusConfig: Record<string, { label: string; color: string }> = {
  PENDING: { label: 'Na čekanju', color: 'default' },
  RECEIVED: { label: 'Primljeno', color: 'blue' },
  AWAITING_REVIEW: { label: 'Čeka pregled', color: 'orange' },
  APPROVED: { label: 'Odobreno', color: 'green' },
  REJECTED: { label: 'Odbijeno', color: 'red' },
  SHARED: { label: 'Deljeno', color: 'purple' },
}

interface DocumentsTabProps {
  labResults: LabResult[] | undefined
}

export function DocumentsTab({ labResults }: DocumentsTabProps) {
  // Collect all documents from lab results
  const allDocuments = labResults?.flatMap(lab =>
    (lab.documents || []).map(doc => ({
      ...doc,
      labName: lab.labName,
      labId: lab.id,
      labStatus: lab.status,
    }))
  ) || []

  if (!labResults?.length && !allDocuments.length) {
    return (
      <Card>
        <Empty description="Nema dokumenata" />
      </Card>
    )
  }

  return (
    <div>
      {/* Lab Results */}
      {labResults && labResults.length > 0 && (
        <Card
          title={
            <Space>
              <ExperimentOutlined />
              <span>Laboratorijski rezultati ({labResults.length})</span>
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          <List
            size="small"
            dataSource={labResults}
            renderItem={(lab) => (
              <List.Item
                extra={
                  <Tag color={statusConfig[lab.status]?.color || 'default'}>
                    {statusConfig[lab.status]?.label || lab.status}
                  </Tag>
                }
              >
                <List.Item.Meta
                  avatar={<ExperimentOutlined style={{ fontSize: 24, color: '#52c41a' }} />}
                  title={lab.labName}
                  description={`Datum uzorka: ${formatDate(lab.sampleDate)}`}
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* Documents */}
      {allDocuments.length > 0 && (
        <Card
          title={
            <Space>
              <FileOutlined />
              <span>Dokumenti ({allDocuments.length})</span>
            </Space>
          }
        >
          <List
            size="small"
            dataSource={allDocuments}
            renderItem={(doc) => {
              const isPdf = doc.fileType?.toLowerCase().includes('pdf')
              const isImage = doc.fileType?.toLowerCase().includes('image') ||
                /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.fileName)

              const FileIcon = isPdf ? FilePdfOutlined : isImage ? FileImageOutlined : FileOutlined
              const iconColor = isPdf ? '#ff4d4f' : isImage ? '#1890ff' : '#8c8c8c'

              return (
                <List.Item
                  actions={[
                    <Button
                      key="view"
                      type="text"
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => window.open(doc.fileUrl, '_blank')}
                    >
                      Prikaži
                    </Button>,
                    <Button
                      key="download"
                      type="text"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() => {
                        const link = document.createElement('a')
                        link.href = doc.fileUrl
                        link.download = doc.fileName
                        link.click()
                      }}
                    >
                      Preuzmi
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<FileIcon style={{ fontSize: 24, color: iconColor }} />}
                    title={doc.fileName}
                    description={
                      <Space size={4} split={<Text type="secondary">•</Text>}>
                        <Text type="secondary">{doc.labName}</Text>
                        {doc.fileSize && (
                          <Text type="secondary">{formatFileSize(doc.fileSize)}</Text>
                        )}
                        <Text type="secondary">
                          {new Date(doc.createdAt).toLocaleDateString('sr-Latn-RS')}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )
            }}
          />
        </Card>
      )}
    </div>
  )
}
